import { useState, useEffect, useRef } from 'react';
import { Link } from '@tanstack/react-router';
import { Users } from '@ui/icons';
import { useGroups, type Group } from '@entities/group';
import { useSession } from '@entities/session';
import { toShortName } from '@shared/lib';
import { SearchInput, type SearchInputProps } from '@ui';

function capitalize(str: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function highlightText(text: string, highlight: string): React.ReactNode {
  if (!highlight.trim()) {
    return text;
  }
  const regex = new RegExp(`(${highlight.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, index) =>
        regex.test(part) ? (
          <span key={index} className="font-bold text-teal">
            {part}
          </span>
        ) : (
          part
        ),
      )}
    </>
  );
}

const ROLE_DISPLAY_MAP: Record<string, string> = {
  owner: 'Contributor',
  contributor: 'Member',
  member: 'Member',
  viewer: 'View Only',
  'view only': 'View Only',
  guest: 'Guest',
};

function formatMemberRole(role: string | null | undefined): string {
  if (!role) return 'Member';
  const key = role.trim().toLowerCase();
  return ROLE_DISPLAY_MAP[key] ?? role;
}

function getGroupCreatorName(group: Group, currentUserId?: string): string {
  if (group.createdById) {
    if (currentUserId && group.createdById === currentUserId) {
      return 'You';
    }
    const creatorMember = group.members.find((m) => m.id === group.createdById);
    if (creatorMember?.name) {
      return toShortName(creatorMember.name);
    }
  }

  const creatorRoleMember = group.members.find(
    (m) => m.role?.trim().toLowerCase() === 'creator' || m.role?.trim().toLowerCase() === 'owner',
  );
  if (creatorRoleMember) {
    if (currentUserId && creatorRoleMember.id === currentUserId) {
      return 'You';
    }
    return toShortName(creatorRoleMember.name);
  }

  return 'You';
}

interface GroupSearchResultsDropdownProps {
  value: string;
  onClear?: (() => void) | undefined;
  setIsOpen: (isOpen: boolean) => void;
}

function GroupSearchResultsDropdown({
  value,
  onClear,
  setIsOpen,
}: GroupSearchResultsDropdownProps) {
  const { data: groups } = useGroups();
  const { data: sessionUser } = useSession();
  const queryLower = value.toLowerCase().trim();

  const matchedGroups =
    groups?.filter((group) => {
      if (!queryLower) return false;
      if (group.name.toLowerCase().includes(queryLower)) return true;
      if (group.type.toLowerCase().includes(queryLower)) return true;
      return group.members.some((m) => m.name.toLowerCase().includes(queryLower));
    }) ?? [];

  if (matchedGroups.length === 0) return null;

  return (
    <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-[300px] overflow-y-auto rounded-[20px] bg-surface-raised border border-border shadow-overlay">
      {matchedGroups.map((group) => {
        const hasMatchedMembers = group.members.some((m) =>
          m.name.toLowerCase().includes(queryLower),
        );

        let additionalInfoNode: React.ReactNode;
        if (hasMatchedMembers) {
          const matchingMembers = group.members.filter((m) =>
            m.name.toLowerCase().includes(queryLower),
          );
          // Member names display as "First L" (mobile's shortName convention) — matching stays
          // against the FULL name above, so searching a last name still finds the group.
          const membersText = matchingMembers
            .map((m) => `${toShortName(m.name)} (${formatMemberRole(m.role)})`)
            .join(', ');
          additionalInfoNode = highlightText(membersText, value);
        } else {
          const creatorName = getGroupCreatorName(group, sessionUser?.id);
          additionalInfoNode = `Created by ${creatorName}`;
        }

        return (
          <Link
            key={group.id}
            to="/groups/$groupId"
            params={{ groupId: group.id }}
            onClick={() => {
              if (onClear) onClear();
              setIsOpen(false);
            }}
            className="flex gap-3 px-4 py-5 border-b border-border last:border-0 hover:bg-canvas/40 transition-colors cursor-pointer text-left"
          >
            <Users className="mt-0.5 size-5 shrink-0 text-[#797DC5]" />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-[16px] leading-[17px] text-fg font-semibold">
                {highlightText(group.name, value)}
              </span>
              <span className="text-sm leading-[17px] text-[#161616] font-light">
                {group.memberCount} Member | {highlightText(capitalize(group.type), value)} |{' '}
                {additionalInfoNode}
              </span>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

export function GroupSearchInput({ onClear, value, ...props }: SearchInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const showPopover = isOpen && typeof value === 'string' && value.trim().length > 0;

  return (
    <div ref={containerRef} className="relative w-full">
      <SearchInput value={value} onClear={onClear} onFocus={() => setIsOpen(true)} {...props} />
      {showPopover && (
        <GroupSearchResultsDropdown value={value} onClear={onClear} setIsOpen={setIsOpen} />
      )}
    </div>
  );
}
